import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './shared/layout.css';
import './PerformanceTab.css';
import { STUDIO_FEATURES } from './perf/nomenclatureParser';
import { ACTION_PRIORITY } from './perf/performanceRules';
import { parseCSV, aggregateByCorIdAndCategory } from './perf/csvParserV2';
import { calculateKPIs, calculateOverviewKPIs } from './perf/kpiCalculator';
import { findTaggedWinners } from './perf/performanceRules';
import { saveAds, loadAds, getActionedSet, markActioned as idbMarkActioned } from './perf/indexedDBStore';
import HeadlineKPIs from './perf/HeadlineKPIs';
import WeeklyTestsBlock from './perf/WeeklyTestsBlock';
import MainAdsetGrid, { MainDatePicker } from './perf/MainAdsetGrid';
import FunnelBlock from './perf/FunnelBlock';
import SpendReportModal from './perf/SpendReportModal';
import SendToUAModal from './perf/SendToUAModal';
import WinnersToPromoteCard from './perf/WinnersToPromoteCard';
import { Icon } from './perf/icons';
import PerfBlockHeader from './perf/PerfBlockHeader';
import AskTimbo from './perf/AskTimbo';
import TokenHealthBanner from './perf/TokenHealthBanner';
import {
  fetchMetaInsights,
  fetchMetaInsightsIncremental,
  fetchLatestSnapshot,
  retryFailedDaysInBackground,
  reaggregateFromCache,
  thumbUrl,
  rangeToDates,
  videoInfoUrl,
  isSyncStale,
  setLastSyncAt,
  timeSinceLastSync
} from './perf/metaApiSource';
import DateRangePicker, { defaultRange, formatLabel as formatRangeLabel } from './perf/DateRangePicker';
import { isCreatorAd } from './creatorType';

const euros = (n) => n >= 1000 ? '€' + (n/1000).toFixed(1).replace('.0','') + 'k' : '€' + Math.round(n).toLocaleString('en-US');
const DATE_RANGES = ['1d', '3d', '7d', '14d', '30d', '90d', '6m', 'all'];

// Error Boundary to catch render crashes in children
class PerfErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(err, info) { console.error('[PerfErrorBoundary]', err, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 20, margin: '12px 0', background: '#fef2f2', border: '1px solid #dc2626', borderRadius: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#dc2626', marginBottom: 8 }}>Render Error in Performance</div>
          <div style={{ fontSize: 12, color: '#666' }}>{this.state.error.message}</div>
          <pre style={{ fontSize: 10, color: '#888', marginTop: 8, whiteSpace: 'pre-wrap' }}>{this.state.error.stack?.slice(0, 400)}</pre>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 12, padding: '6px 14px', border: '1px solid #dc2626', borderRadius: 6, background: '#fff', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}>Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function EmptyState({ icon, text, hint }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon}</div>
      <div className="empty-text">{text}</div>
      <div className="empty-hint">{hint}</div>
    </div>
  );
}

export default function PerformanceTab({ onToast, onNavigateToStudio }) {
  const [dateRange, setDateRange] = useState(defaultRange);
  const [featureRange, setFeatureRange] = useState(defaultRange);
  const [mainThreshold, setMainThreshold] = useState({ minSpend: 2000, maxCpa: 50 });
  const [showSpend, setShowSpend] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [mainView, setMainView] = useState('grid');
  const [activeFilter, setActiveFilter] = useState(null);
  const [importing, setImporting] = useState(false);
  const [modalCreative, setModalCreative] = useState(null);
  const [featFilter, setFeatFilter] = useState({ testing: 'all', main: 'all' });
  const [thumbSize, setThumbSize] = useState({ testing: 0, main: 0 });
  const fileInputRef = useRef(null);
  const [v2AggAds, setV2AggAds] = useState([]);
  const [winnersAggAds, setWinnersAggAds] = useState([]); // fenêtre winners 6 mois (cache), indépendante de la vue
  const [videoAdId, setVideoAdId] = useState(null);
  const [weeklyGroupBy, setWeeklyGroupBy] = useState('week');
  const [weeklyView, setWeeklyView] = useState('list');
  const [mainPreset, setMainPreset] = useState('10d');
  const [actionedSet, setActionedSet] = useState(new Set());
  const [isSyncingMeta, setIsSyncingMeta] = useState(false);
  const [metaSyncError, setMetaSyncError] = useState(null);
  const [lastSyncDisplay, setLastSyncDisplay] = useState(timeSinceLastSync());
  const [showSendToUA, setShowSendToUA] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null); // { done, total, phase } or null when idle
  const [manualImportAt, setManualImportAt] = useState(null); // last CSV import timestamp (Fix 6)
  const manualImportAtRef = useRef(null); // Fix 3 v3.99.5: ref always in-sync for async closures

  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    // Always load actioned set
    getActionedSet().then(setActionedSet).catch(() => {});

    // v3.99.8 Phase 2: read R2 snapshot first (fast path, no Meta API call).
    // Cron service refreshes this at 9h CET daily.
    fetchLatestSnapshot()
      .then(async (result) => {
        if (result?.rows && result.rows.length > 0) {
          const aggregated = aggregateByCorIdAndCategory(result.rows);
          await saveAds(aggregated);
          setV2AggAds(aggregated);
          const syncedAt = result.meta?.synced_at ? new Date(result.meta.synced_at).getTime() : Date.now();
          setLastSyncAt(syncedAt);
          setLastSyncDisplay(timeSinceLastSync());
          console.log(`[mount] Loaded ${aggregated.length} ads from R2 snapshot (synced ${result.meta?.synced_at})`);
          return;
        }
        // Fallback: IndexedDB
        const ads = await loadAds().catch(() => []);
        if (ads && ads.length) setV2AggAds(ads);
      })
      .catch(async () => {
        const ads = await loadAds().catch(() => []);
        if (ads && ads.length) setV2AggAds(ads);
      });
  }, []);

  async function handleMetaSync(opts = {}) {
    const { silent = false } = opts;
    setIsSyncingMeta(true);
    setMetaSyncError(null);
    setSyncProgress({ done: 0, total: 0, phase: 'starting' });

    try {
      const { since, until } = rangeToDates(dateRange);
      const result = await fetchMetaInsightsIncremental(since, until, {
        withThumbnails: true,
        onProgress: (done, total, phase) => setSyncProgress({ done, total, phase }),
      });

      // Even if some days failed, we may have partial data in result.rows
      if (!result.rows) {
        setMetaSyncError(result.error || 'Sync failed');
        if (!silent && onToastRef.current) onToastRef.current(`✕ Meta sync failed: ${result.error}`);
        return;
      }

      const aggregated = aggregateByCorIdAndCategory(result.rows);

      // Fix 3 v3.99.5: do NOT overwrite v2AggAds if user imported a CSV during this in-flight sync
      const importTimestamp = manualImportAtRef.current;
      if (importTimestamp && (Date.now() - importTimestamp) < 5 * 60 * 1000) {
        console.log('[meta-sync] Skipping setV2AggAds — CSV imported during sync, keeping CSV data');
        const m2 = result.meta || {};
        if ((m2.failed_days || 0) === 0) {
          setLastSyncAt();
          setLastSyncDisplay(timeSinceLastSync());
        }
        return;
      }

      await saveAds(aggregated);
      setV2AggAds(aggregated);

      const m = result.meta || {};
      const failed = m.failed_days || 0;
      const fetched = m.fetched_days || 0;
      const cached = m.cached_days || 0;

      // Fix 5: only mark sync as complete when ALL days succeeded
      if (failed === 0) {
        setLastSyncAt();
        setLastSyncDisplay(timeSinceLastSync());
      }

      if (!silent && onToastRef.current) {
        const msg = failed > 0
          ? `⚠ Meta sync: ${aggregated.length} ads · ${fetched} fetched, ${cached} cached, ${failed} failed (retrying…)`
          : `✓ Meta sync: ${aggregated.length} ads · ${fetched} fetched, ${cached} cached`;
        onToastRef.current(msg);
      }
      if (result.error) setMetaSyncError(result.error);

      // Fix 5: transparent background retry for failed days
      if (failed > 0) {
        const failedDates = (m.failures || []).map(f => f.date).filter(Boolean);
        const { since: snc, until: unt } = rangeToDates(dateRange);
        retryFailedDaysInBackground(failedDates, { withThumbnails: true })
          .then(async (stillFailed) => {
            if (stillFailed.length === 0) {
              const transformedRows = await reaggregateFromCache(snc, unt);
              const aggregatedNew = aggregateByCorIdAndCategory(transformedRows);
              await saveAds(aggregatedNew);
              setV2AggAds(aggregatedNew);
              setLastSyncAt();
              setLastSyncDisplay(timeSinceLastSync());
              setMetaSyncError(null);
              console.log('[meta-retry] All failed days recovered in background');
            } else {
              console.warn(`[meta-retry] ${stillFailed.length} day(s) still failing after 3 attempts:`, stillFailed);
            }
          })
          .catch(err => console.error('[meta-retry] Background retry crashed:', err));
      }
    } catch (err) {
      const msg = err.message || 'Unknown error';
      setMetaSyncError(msg);
      if (!silent && onToastRef.current) onToastRef.current(`✕ Meta sync failed: ${msg}`);
    } finally {
      setIsSyncingMeta(false);
      setTimeout(() => setSyncProgress(null), 1500);
    }
  }

  // v3.99.8 Phase 2: auto-sync-on-mount removed.
  // Snapshot from R2 is loaded in the initial mount useEffect (cron-refreshed at 9h CET).
  // Meta API is only hit on:
  //   - explicit Refresh button click
  //   - dateRange change to a range OUTSIDE the last 7-day snapshot window

  // Re-sync when date range falls outside snapshot coverage (last 7 days).
  useEffect(() => {
    // Skip if user just imported a CSV (within 5 minutes)
    if (manualImportAt && (Date.now() - manualImportAt) < 5 * 60 * 1000) return;
    if (!dateRange || typeof dateRange !== 'object' || !dateRange.since || !dateRange.until) return;

    const today = new Date().toISOString().slice(0, 10);
    const sevenDaysAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
    const isWithinSnapshot = dateRange.since >= sevenDaysAgo && dateRange.until <= today;
    if (isWithinSnapshot) return; // snapshot covers it, no Meta call needed

    if (isSyncStale()) {
      handleMetaSync({ silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, manualImportAt]);

  // Update du "X min ago" toutes les 30s
  useEffect(() => {
    const interval = setInterval(() => {
      setLastSyncDisplay(timeSinceLastSync());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Single source of truth: V2 overview KPIs filtered by period
  const overviewKPIs = useMemo(
    () => calculateOverviewKPIs(v2AggAds, dateRange),
    [v2AggAds, dateRange]
  );
  const enrichedAds = overviewKPIs?.enrichedAds || [];
  const hasData = overviewKPIs !== null;

  // Filter to influencer ads only (single source of truth for all blocks).
  // Backward-compat: matches old cached ad_owner values via regex on ad_name_sample.
  const isInfluencerAd = (ad) => {
    if (ad.ad_owner === 'influencer') return true;
    return isCreatorAd(ad.ad_name_sample);
  };
  const influencerEnrichedAds = useMemo(
    () => (overviewKPIs?.enrichedAds || []).filter(isInfluencerAd),
    [overviewKPIs]
  );
  const mainAds = useMemo(() => influencerEnrichedAds.filter(c => c.phase === 'main'), [influencerEnrichedAds]);

  // v3.99.6 Fix 6 — ALL influencer ads NOT filtered by global dateRange — for MainAdsetGrid's local picker
  const allInfluencerAds = useMemo(
    () => (v2AggAds || []).filter(isInfluencerAd),
    [v2AggAds]
  );

  // Recompute Today's to-do counts from influencer-only ads (overrides overviewKPIs.todo which counts ALL ads)
  const influencerTodoCounts = useMemo(() => {
    let promote = 0, iterate = 0, fatigue = 0, localize = 0;
    for (const ad of influencerEnrichedAds) {
      if (ad.action === 'PROMOTE') promote++;
      else if (ad.action === 'ITERATE') iterate++;
      else if (ad.action === 'FATIGUE') fatigue++;
      else if (ad.action === 'LOCALIZE') localize++;
    }
    return { promote, iterate, fatigue, localize };
  }, [influencerEnrichedAds]);

  // Headline KPIs (current period) — INFLUENCER ONLY (Fix 4)
  const headlineKpis = useMemo(() => {
    if (!overviewKPIs) return null;
    const ads = influencerEnrichedAds;
    let totalSpend = 0, totalPurchases = 0, active = 0;
    for (const ad of ads) {
      totalSpend += ad.spend || 0;
      totalPurchases += ad.achats || 0;
      if (ad.delivery && ad.delivery.toLowerCase().includes('activ')) active++;
    }
    return {
      spend: totalSpend,
      purchases: totalPurchases,
      avgCpa: totalPurchases > 0 ? totalSpend / totalPurchases : null,
      activeAds: active,
    };
  }, [overviewKPIs, influencerEnrichedAds]);

  // Previous-period KPIs (N-1 window of same length) for deltas
  const prevKpis = useMemo(() => {
    if (!v2AggAds.length) return null;
    if (!dateRange || typeof dateRange !== 'object' || !dateRange.since || !dateRange.until) return null;
    const since = new Date(dateRange.since + 'T00:00:00Z');
    const until = new Date(dateRange.until + 'T00:00:00Z');
    const lengthDays = Math.round((until - since) / 86400000) + 1;
    if (!lengthDays || lengthDays <= 0) return null;
    const prevUntil = new Date(since); prevUntil.setUTCDate(prevUntil.getUTCDate() - 1);
    const prevSince = new Date(prevUntil); prevSince.setUTCDate(prevSince.getUTCDate() - (lengthDays - 1));
    const prevSinceStr = prevSince.toISOString().slice(0, 10);
    const prevUntilStr = prevUntil.toISOString().slice(0, 10);
    let spend = 0, purchases = 0, active = 0;
    for (const ad of v2AggAds) {
      if (!ad.last_date) continue;
      if (ad.last_date < prevSinceStr || ad.last_date > prevUntilStr) continue;
      // Fix 4: influencer-only filter for prevKpis too (consistency with current period)
      if (ad.ad_owner !== 'influencer' && !isCreatorAd(ad.ad_name_sample)) continue;
      spend += ad.spend || 0;
      purchases += ad.achats || 0;
      if (ad.delivery && ad.delivery.toLowerCase().includes('activ')) active++;
    }
    return {
      spend, purchases,
      avgCpa: purchases > 0 ? spend / purchases : null,
      activeAds: active,
    };
  }, [v2AggAds, dateRange]);


  const v2Kpis = useMemo(() => v2AggAds.length > 0 ? calculateKPIs(v2AggAds) : null, [v2AggAds]);

  // ── Winners scannés sur une fenêtre FIXE de 6 mois, indépendante du date-picker Overview.
  // Lus depuis le cache local (aucun appel réseau) → un potentiel d'il y a 5 mois remonte quand même.
  const refreshWinnersWindow = useCallback(async () => {
    try {
      const until = new Date().toISOString().slice(0, 10);
      const s6 = new Date(); s6.setMonth(s6.getMonth() - 6);
      const since = s6.toISOString().slice(0, 10);
      const rows = await reaggregateFromCache(since, until);
      if (rows && rows.length) setWinnersAggAds(aggregateByCorIdAndCategory(rows));
    } catch (e) { /* cache vide/indispo → fallback sur la plage courante */ }
  }, []);
  useEffect(() => { refreshWinnersWindow(); }, [v2AggAds, refreshWinnersWindow]);

  const winnersSource = winnersAggAds.length ? winnersAggAds : v2AggAds;
  // Tagged NEW / STILL_VALID / LEGACY; the pushable pile (today's rule) excludes LEGACY.
  const taggedWinners = useMemo(() => findTaggedWinners(winnersSource), [winnersSource]);
  const winnersToPromote = useMemo(
    () => taggedWinners.filter(w => w.winner_tag !== 'LEGACY').sort((a, b) => (b.achats || 0) - (a.achats || 0)),
    [taggedWinners]
  );
  const winnersKpis = useMemo(
    () => winnersSource.length > 0 ? calculateKPIs(winnersSource) : v2Kpis,
    [winnersSource, v2Kpis]
  );

  // Weekly tests over a 6-month window so each week/month shows its own winners.
  const weeklyEnriched = useMemo(() => {
    const src = winnersAggAds.length ? winnersAggAds : v2AggAds;
    if (!src.length) return [];
    const until = new Date().toISOString().slice(0, 10);
    const s6 = new Date(); s6.setMonth(s6.getMonth() - 6);
    const since = s6.toISOString().slice(0, 10);
    return calculateOverviewKPIs(src, { since, until }).enrichedAds || [];
  }, [winnersAggAds, v2AggAds]);

  // Funnel + Perf-by-feature (independent date) data
  const funnelPromotedAds = useMemo(() => weeklyEnriched.filter(c => isInfluencerAd(c) && c.phase === 'main'), [weeklyEnriched]);
  const funnelTested = useMemo(() => weeklyEnriched.filter(c => isInfluencerAd(c) && c.phase === 'testing').length, [weeklyEnriched]);
  const featurePerfByFeature = useMemo(() => (calculateOverviewKPIs(v2AggAds, featureRange)?.perfByFeature) || [], [v2AggAds, featureRange]);
  const topFeat = useMemo(() => (featurePerfByFeature || []).find(f => f.hasFeatData) || (featurePerfByFeature || [])[0] || null, [featurePerfByFeature]);
  const handleMarkActioned = useCallback(async (core_id) => {
    await idbMarkActioned(core_id);
    const updated = await getActionedSet();
    setActionedSet(updated);
  }, []);

  const onToastRef = useRef(onToast);
  useEffect(() => { onToastRef.current = onToast; }, [onToast]);
  useEffect(() => { manualImportAtRef.current = manualImportAt; }, [manualImportAt]);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const rawRows = await parseCSV(file);
      const aggregated = aggregateByCorIdAndCategory(rawRows);
      await saveAds(aggregated);
      setV2AggAds(aggregated);
      setManualImportAt(Date.now()); // Fix 6: protect CSV from auto-sync overwrite for 5min
      if (onToastRef.current) onToastRef.current(`✓ Imported ${aggregated.length} ads`);
    } catch (err) {
      console.error('Import error:', err);
      if (onToastRef.current) onToastRef.current('✕ Import error: ' + err.message);
    } finally {
      setImporting(false);
    }
  }, []);

  useEffect(() => {
    const onDragOver = (e) => { e.preventDefault(); };
    const onDrop = (e) => { e.preventDefault(); const file = e.dataTransfer.files[0]; if (file && file.name.endsWith('.csv')) handleFile(file); };
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', onDrop);
    return () => { document.removeEventListener('dragover', onDragOver); document.removeEventListener('drop', onDrop); };
  }, [handleFile]);

  const openFilePicker = () => fileInputRef.current?.click();

  const handleAction = useCallback((creative, slug) => {
    if (!creative) return;
    const toast = onToastRef.current || ((m) => console.log(m));
    if (slug === 'open-meta' || slug === 'promote') {
      if (creative.metaAdId) window.open(`https://business.facebook.com/adsmanager/manage/ads?selected_ad_ids=${creative.metaAdId}`, '_blank', 'noopener');
      if (slug === 'promote') toast('↗ Opening Meta Ads Manager · duplicate into Main adset');
      return;
    }
    if (slug === 'remix') { if (onNavigateToStudio) onNavigateToStudio(null, creative.nomenclature?.feature, creative.id); else toast('+ Open Studio to remix'); return; }
    if (slug === 'localize') { toast('🌐 Localize · Spanish variant for ' + (creative.nomenclature?.creator || (creative.ad_name_sample || '').slice(0, 30))); return; }
  }, [onNavigateToStudio]);

  const toggleFilter = (action) => { setActiveFilter(prev => prev === action ? null : action); };

  const testingCreatives = useMemo(() => {
    const p = ACTION_PRIORITY.testing;
    return enrichedAds.filter(c => c.phase === 'testing').sort((a, b) => { const pa = p[a.action] || 99; const pb = p[b.action] || 99; if (pa !== pb) return pa - pb; return b.spend - a.spend; });
  }, [enrichedAds]);

  const mainCreatives = useMemo(() => {
    const p = ACTION_PRIORITY.main;
    return enrichedAds.filter(c => c.phase === 'main').sort((a, b) => { const pa = p[a.action] || 99; const pb = p[b.action] || 99; if (pa !== pb) return pa - pb; return b.spend - a.spend; });
  }, [enrichedAds]);

  return (
    <div className="perf-tab tb-page">
      <input ref={fileInputRef} type="file" accept=".csv" style={{display: 'none'}} onChange={(e) => handleFile(e.target.files?.[0])} />

      {/* HEADER — global */}
      <TokenHealthBanner recheckKey={metaSyncError} />
      <AskTimbo onPlay={setVideoAdId} />

      {/* ═══ BLOC 1 — OVERVIEW ═══════════════════════════════════════════ */}
      <section className="perf-block tb-card perf-block-overview">
        <PerfBlockHeader
          icon={<Icon name="overview" />}
          title="Overview"
          actions={<>
            {/* Data state lives with the data it describes, not on the Ask bar. */}
            <span className={'tb-sync' + (metaSyncError ? ' err' : isSyncingMeta ? ' busy' : '')} title={metaSyncError || undefined}>
              <i className="tb-sync-dot" />
              {isSyncingMeta
                ? (syncProgress && syncProgress.total > 0 ? `Syncing ${syncProgress.done}/${syncProgress.total} days…` : 'Syncing…')
                : metaSyncError ? `Sync error${lastSyncDisplay ? ' · last synced ' + lastSyncDisplay : ''}`
                : lastSyncDisplay ? `Synced ${lastSyncDisplay}` : 'Not synced yet'}
            </span>
            <button type="button" className="tb-ctl" onClick={() => handleMetaSync({ silent: false })} disabled={isSyncingMeta} title="Refresh from the Meta Ads API">
              <span className={isSyncingMeta ? 'perf-sync-spin' : undefined}>⟳</span> Refresh
            </button>
            <button type="button" className="tb-ctl" onClick={openFilePicker} disabled={importing} title="Import a Meta CSV export (fallback when the API is down)">
              ↑ {importing ? 'Importing…' : 'Import CSV'}
            </button>
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </>}
        />
        <div className="perf-block-body">
          <div className="ov-grid">
            <OCard label="Spend" value={hasData ? ovEuro(headlineKpis?.spend) : '—'} hint="view ↗" onClick={() => setShowSpend(true)} />
            <OCard label="Perf by features" value={topFeat ? topFeat.name : '—'} hint="view ↗" onClick={() => setShowFeatures(true)} />
            <OCard label="Purchases" value={hasData ? ovNum(headlineKpis?.purchases) : '—'} />
            <OCard label="Avg CPA" value={hasData ? ovEuro(headlineKpis?.avgCpa) : '—'} />
          </div>
          <div className="ov-grid">
            <OCard label="Active ads" value={hasData ? ovNum(headlineKpis?.activeAds) : '—'} />
            <OCard label="Promote" value={influencerTodoCounts.promote} sub="testing winners" onClick={() => toggleFilter('PROMOTE')} active={activeFilter === 'PROMOTE'} />
            <OCard label="Iterate" value={influencerTodoCounts.iterate} sub="main winners" onClick={() => toggleFilter('ITERATE')} active={activeFilter === 'ITERATE'} />
            <OCard label="Localize" value={influencerTodoCounts.localize} sub="to ES/FR/JA" onClick={() => toggleFilter('LOCALIZE')} active={activeFilter === 'LOCALIZE'} />
          </div>
          {activeFilter && (
            <div style={{ marginTop: 12, border: '1px solid rgba(255,121,0,0.28)', borderRadius: 11, background: '#fffaf4', padding: '13px 15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#8a4400' }}>
                  {influencerEnrichedAds.filter(c => c.action === activeFilter).length} creative(s) to {activeFilter.toLowerCase()}
                </span>
                <span onClick={() => setActiveFilter(null)} style={{ cursor: 'pointer', color: '#b3854d', fontSize: 12, fontWeight: 700 }}>close ✕</span>
              </div>
              {influencerEnrichedAds.filter(c => c.action === activeFilter).length === 0 ? (
                <div style={{ color: '#a07a4a', fontSize: 12 }}>None right now.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                  {influencerEnrichedAds.filter(c => c.action === activeFilter).slice(0, 12).map(ad => (
                    <div key={ad.core_id || ad.ad_name_sample} style={{ border: '1px solid #eee', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                      <div onClick={() => ad.ad_id && setVideoAdId(ad.ad_id)} style={{ position: 'relative', aspectRatio: '9 / 16', background: '#f0f0f0', cursor: ad.ad_id ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {thumbUrl(ad)
                          ? <img src={thumbUrl(ad)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ color: '#bbb', fontSize: 24 }}>▶</span>}
                      </div>
                      <div onClick={() => setModalCreative(ad)} style={{ padding: '7px 9px', cursor: 'pointer' }}>
                        <div style={{ fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(ad.nomenclature?.creator || (ad.ad_name_sample || '').split('_')[0] || '—').replace(/_/g, ' ')}</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 10.5, color: '#999' }}>
                          <span><b>{ad.achats ?? 0}</b> ach.</span>
                          <span><b>€{ad.cpa != null ? Math.round(ad.cpa) : '—'}</b></span>
                          <span><b>€{Math.round(ad.spend || 0)}</b></span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ═══ BLOC 2 — WEEKLY TESTS ═══════════════════════════════════════ */}
      <section className="perf-block tb-card perf-block-weekly">
        <PerfBlockHeader
          icon={<Icon name="weekly" />}
          title="Weekly tests"
          actions={<>
            <div className="perf-hicons">
              {['list', 'grid'].map(m => (
                <button key={m} type="button" onClick={() => setWeeklyView(m)} title={m === 'grid' ? 'Thumbnails' : 'List'} className={weeklyView === m ? 'on' : ''}>
                  {m === 'grid' ? '▦' : '≡'}
                </button>
              ))}
            </div>
            <GroupByPicker value={weeklyGroupBy} onChange={setWeeklyGroupBy} />
          </>}
        />
        <div className="perf-block-body">
          <WeeklyTestsBlock enrichedAds={weeklyEnriched} groupBy={weeklyGroupBy} viewMode={weeklyView} onOpenCreative={setModalCreative} onPlay={setVideoAdId} />
        </div>
      </section>

      {/* FUNNEL — test → spend */}
      <FunnelBlock tested={funnelTested} winnersCount={winnersToPromote.length + funnelPromotedAds.length} promotedAds={funnelPromotedAds} lostPotentialEUR={winnersKpis?.manqueAGagnerEUR ?? 0} threshold={mainThreshold} onThreshold={setMainThreshold} />

      {/* ═══ WINNERS TO PROMOTE — manque à gagner (restauré) ══════ */}

      {/* ═══ BLOC 3 — MAIN ASSETS ════════════════════════════════════════ */}
      <section className="perf-block tb-card perf-block-main">
        <PerfBlockHeader
          icon={<Icon name="main" />}
          title="Main assets"
          actions={<>
            <div className="perf-hicons">
              {['grid', 'list'].map(m => (
                <button key={m} type="button" onClick={() => setMainView(m)} title={m} className={mainView === m ? 'on' : ''}>{m === 'grid' ? '▦' : '≡'}</button>
              ))}
            </div>
            <MainDatePicker preset={mainPreset} onChange={setMainPreset} />
          </>}
        />
        <div className="perf-block-body perf-block-body-flush">
          <MainAdsetGrid
            allInfluencerAds={allInfluencerAds}
            globalDateRange={dateRange}
            localPreset={mainPreset}
            threshold={mainThreshold}
            viewMode={mainView}
            onOpenCreative={setModalCreative}
            onPlay={setVideoAdId} />
        </div>
      </section>

      {/* WINNERS TO PROMOTE */}
      {taggedWinners.length > 0 && (
        <WinnersToPromoteCard taggedWinners={taggedWinners} kpis={winnersKpis} onPlay={setVideoAdId} onSendToUA={() => setShowSendToUA(true)} />
      )}

      {/* ═══ PERF BY FEATURE ═══ */}

      {modalCreative && <CreativeModal c={modalCreative} onClose={() => setModalCreative(null)} onAction={handleAction} />}
      <SpendReportModal open={showSpend} onClose={() => setShowSpend(false)} />
      {showFeatures && (
        <div onClick={() => setShowFeatures(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 760, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <PerfBlockHeader
              variant="modal"
              title="Perf by features"
              actions={<DateRangePicker value={featureRange} onChange={setFeatureRange} />}
              onClose={() => setShowFeatures(false)}
            />
            <div style={{ padding: 20 }}>
              <FeatureListV2 perfByFeature={featurePerfByFeature} />
            </div>
          </div>
        </div>
      )}
      <VideoModal adId={videoAdId} onClose={() => setVideoAdId(null)} />

      <SendToUAModal
        isOpen={showSendToUA}
        onClose={() => setShowSendToUA(false)}
        winnersToPromote={winnersToPromote}
        kpis={winnersKpis}
        actionedSet={actionedSet}
        onMarkActioned={handleMarkActioned} />
    </div>
  );
}

function TodoMini({ filter, count, label, sub, activeFilter, onClick, hasData, icon }) {
  const isActive = activeFilter === filter;
  const isDisabled = activeFilter && activeFilter !== filter;
  const cls = filter.toLowerCase();
  // v3.99.5 Fix 1: DOM order matches HeadlineKPIs — label → num → sub. Icon dropped (unused in final design).
  return (
    <div className={'perf-todo-mini' + (isActive ? ' active ' + cls : '') + (isDisabled ? ' disabled' : '')} onClick={() => hasData && onClick(filter)} style={{cursor: hasData ? 'pointer' : 'default'}}>
      <div className="mini-label">{label}</div>
      <div className={'mini-num ' + cls + (hasData ? '' : ' empty')}>{hasData ? count : '—'}</div>
      <div className="mini-sub">{sub}</div>
    </div>
  );
}

function VideoModal({ adId, onClose }) {
  const [url, setUrl] = useState(null);
  const [iframe, setIframe] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!adId) return;
    setUrl(null); setIframe(null); setErr(false);
    let alive = true;
    fetch(videoInfoUrl(adId))
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(d => {
        if (!alive) return;
        if (d.url) setUrl(d.url);
        else if (d.iframe) setIframe(d.iframe);
        else setErr(true);
      })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [adId]);
  if (!adId) return null;
  const box = { position: 'relative' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
      <div onClick={e => e.stopPropagation()} style={{ maxWidth: '92vw', ...box }}>
        <span onClick={onClose} style={{ position: 'absolute', top: -34, right: 0, color: '#fff', fontSize: 26, cursor: 'pointer' }}>✕</span>
        {url ? (
          <div style={{ width: 320, background: '#000', borderRadius: 14, overflow: 'hidden', aspectRatio: '9 / 16', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <video src={url} controls autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
          </div>
        ) : iframe ? (
          <div style={{ background: '#111', borderRadius: 14, overflow: 'hidden', width: 340, maxHeight: '84vh', overflowY: 'auto' }} dangerouslySetInnerHTML={{ __html: iframe }} />
        ) : err ? (
          <div style={{ width: 320, background: '#000', borderRadius: 14, aspectRatio: '9 / 16', color: '#fff', fontSize: 13, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 30 }}>Preview unavailable for this creative.<br /><a href={'https://business.facebook.com/adsmanager/manage/ads?selected_ad_ids=' + adId} target="_blank" rel="noopener noreferrer" style={{ color: '#FF7900', marginTop: 8 }}>Open in Meta ↗</a></div>
        ) : (
          <div style={{ width: 320, background: '#000', borderRadius: 14, aspectRatio: '9 / 16', color: '#fff', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading…</div>
        )}
      </div>
    </div>
  );
}

function GroupByPicker({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const label = value === 'month' ? 'By month' : 'By week';
  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button type="button" onClick={() => setOpen(v => !v)} className="perf-hctrl">
        {label} <span className="perf-hctrl-chev">▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, boxShadow: '0 4px 14px rgba(0,0,0,0.08)', padding: 4, zIndex: 20, minWidth: 130 }}>
          {[['week', 'By week'], ['month', 'By month']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => { onChange(v); setOpen(false); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6, cursor: 'pointer', color: value === v ? '#FF7900' : '#444', background: value === v ? 'rgba(255,121,0,0.06)' : 'transparent' }}>
              {l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ovEuro(n) { if (n === null || n === undefined || isNaN(n)) return '—'; if (n >= 1000) return '€' + (n / 1000).toFixed(1).replace('.0', '') + 'k'; return '€' + Math.round(n); }
function ovNum(n) { if (n === null || n === undefined || isNaN(n)) return '—'; return Math.round(n).toLocaleString('en-US'); }
function OCard({ label, value, sub, hint, onClick, active }) {
  return (
    <div className={'ov-card' + (onClick ? ' click' : '') + (active ? ' on' : '')} onClick={onClick}>
      <div className="l">{label}{hint && <span className="hint">{hint}</span>}</div>
      <div className="v">{value}</div>
      {sub && <div className="s">{sub}</div>}
    </div>
  );
}

function FeatureListV2({ perfByFeature }) {
  if (!perfByFeature || !perfByFeature.length) return <div className="perf-feat-list"><div style={{ padding: 12, color: '#888', fontSize: 12 }}>No feature data</div></div>;
  return (
    <div className="perf-feat-list">
      {perfByFeature.map(f => {
        const hitPct = f.total > 0 ? f.winners / f.total : 0;
        let hitCls = 'ok'; if (f.total >= 5) { if (hitPct >= 0.15) hitCls = 'good'; else if (hitPct < 0.05) hitCls = 'bad'; }
        let barCls = ''; if (!f.hasFeatData) barCls = 'empty'; else if (f.spendPct > 60) barCls = 'green'; else if (f.spendPct < 5) barCls = 'amber';
        return (<div key={f.key} className="perf-feat-row">
          <span className="feat-name">{f.name}</span>
          <div className="feat-bar-wrap"><div className="feat-bar"><div className={'feat-bar-fill ' + barCls} style={{width: Math.max(f.spendPct, f.hasFeatData ? 2 : 0).toFixed(1) + '%'}}></div></div><span className="feat-share">{f.hasFeatData ? `${f.spendPct >= 10 ? Math.round(f.spendPct) : f.spendPct.toFixed(1)}% spend · ${euros(f.spend)}` : 'No tests yet'}</span></div>
          <div className={'feat-meta' + (f.hasFeatData ? '' : ' empty')}><span className={'hit-rate ' + hitCls}>{f.total > 0 ? `${f.winners}/${f.total}` : '—'}</span><span className="hit-label">{f.hitLabel}</span></div>
        </div>);
      })}
    </div>
  );
}

function PhaseColumn({ title, phase, icon, subtitle, creatives, dateRange, activeFilter, onOpenCreative, hasData,
                      featFilter, onFeatFilter, thumbSize, onThumbSize }) {
  const total = creatives.length;
  const spend = creatives.reduce((s, c) => s + c.spend, 0);
  const featCounts = { all: total };
  creatives.forEach(c => { const f = c.feature || 'UNK'; featCounts[f] = (featCounts[f] || 0) + 1; });
  const visible = featFilter === 'all' ? creatives : creatives.filter(c => c.feature === featFilter);
  const thumbMinPx = Math.round(80 + (thumbSize / 100) * 100);

  const emptyIcon = phase === 'testing' ? '🧪' : '🚀';
  const emptyText = phase === 'testing' ? 'No testing creatives yet' : 'No promoted creatives yet';
  const emptyHint = 'Import your CSV';

  return (
    <div className="section-block">
      <div className="section-header">
        <div className="section-header-left">
          <span className="section-icon">{icon}</span>
          <div className="section-titles">
            <div className="section-title" data-tooltip={subtitle}>{title}</div>
            {hasData && (
              <div className="section-subtitle">
                <strong>{visible.length} creatives</strong> · <strong>{euros(spend)}</strong>
              </div>
            )}
          </div>
        </div>
      </div>
      {hasData && total > 0 && (
        <div className="perf-phase-controls">
          <div className="perf-feature-chips">
            <div className={'chip' + (featFilter === 'all' ? ' active' : '')} onClick={() => onFeatFilter('all')}>All<span className="count">{featCounts.all}</span></div>
            {STUDIO_FEATURES.map(f => (featCounts[f.key] || 0) > 0 && (<div key={f.key} className={'chip' + (featFilter === f.key ? ' active' : '')} onClick={() => onFeatFilter(f.key)}>{f.label.split(' ')[0]}<span className="count">{featCounts[f.key]}</span></div>))}
          </div>
          <div className="perf-size-slider"><span className="slider-label">S</span><input type="range" min="0" max="100" value={thumbSize} onChange={(e) => onThumbSize(+e.target.value)} /><span className="slider-label">L</span></div>
        </div>
      )}
      <div className="perf-thumb-grid" style={{'--thumb-min': thumbMinPx + 'px'}}>
        {!hasData || total === 0 ? (
          <EmptyState icon={emptyIcon} text={emptyText} hint={emptyHint} />
        ) : (
          visible.slice(0, 36).map(c => (<Thumbnail key={c.core_id || c.ad_name_sample} c={c} dimmed={activeFilter && c.action !== activeFilter} onOpen={onOpenCreative} />))
        )}
      </div>
    </div>
  );
}

function Thumbnail({ c, dimmed, onOpen }) {
  const creatorRaw = c.nomenclature?.creator || (c.ad_name_sample || '').split('_')[0] || 'unknown';
  const creatorName = creatorRaw.replace(/_/g, ' ');
  const actionLabel = { PROMOTE: '\u2197 Promote', RETEST: '\u21BB Retest', CUT: '\u2715 Cut', WAIT: '\u25CB Testing', ITERATE: '+ Iterate', LOCALIZE: '\u2295 Localize', FATIGUE: '\u26A0 Fatigue', HEALTHY: '\u25CE Healthy' }[c.action] || '\u25CB';
  return (
    <div className={'perf-thumb' + (dimmed ? ' dimmed' : '')} data-action={c.action} onClick={() => onOpen(c)}>
      <div className="thumb-bg"><div className="thumb-creator">{creatorName}</div></div>
      <div className={'thumb-action ' + (c.action || '').toLowerCase()}>{actionLabel}</div>
      {c.nomenclature?.creator && <div className="thumb-linked" title="Linked to Timbo source">T</div>}
      <div className="thumb-overlay"><div className="thumb-cpa">{c.cpa !== null ? '\u20AC' + Math.round(c.cpa) : '\u2014'}</div><div className="thumb-spend">{euros(c.spend)} spent</div></div>
    </div>
  );
}

function CreativeModal({ c, onClose, onAction }) {
  const tags = []; const nom = c.nomenclature || {};
  if (nom.owner) tags.push({ cls: '', label: 'owner', val: nom.owner });
  if (nom.creator) tags.push({ cls: 'creator', label: 'creator', val: nom.creator });
  if (nom.feature) tags.push({ cls: 'feature', label: 'feature', val: nom.feature });
  if (nom.lang) tags.push({ cls: 'lang', label: 'lang', val: nom.lang });
  if (nom.hook) tags.push({ cls: '', label: 'hook', val: nom.hook });
  if (nom.persona) tags.push({ cls: '', label: 'persona', val: nom.persona });
  if (nom.duration) tags.push({ cls: '', label: 'dur', val: nom.duration + 's' });
  if (c.phase) tags.push({ cls: 'phase-' + c.phase, label: 'phase', val: c.phase });
  if (c.adset_category) tags.push({ cls: '', label: 'adset', val: c.adset_category });

  const adName = c.ad_name_sample || '';

  return (
    <div className="perf-modal-overlay open" onClick={(e) => { if (e.target.classList.contains('perf-modal-overlay')) onClose(); }}>
      <div className="perf-modal">
        <div className="perf-modal-header"><h3>#{nom.metaSeqId || '—'} · {nom.creator || adName.slice(0, 40)}</h3><button className="perf-btn ghost" onClick={onClose}>Close</button></div>
        <div className="perf-modal-body">
          <div className="modal-ad-name">{adName}</div>
          <div className="nomenclature-tags">{tags.map((t, i) => (<span key={i} className={'tag ' + t.cls}><span className="tag-label">{t.label}</span>{t.val}</span>))}</div>
          <div className="metrics-grid">
            <div className="metric"><div className="metric-label">Spend</div><div className="metric-value">{euros(c.spend)}</div></div>
            <div className="metric"><div className="metric-label">Purchases</div><div className="metric-value good">{c.achats ?? 0}</div></div>
            <div className="metric"><div className="metric-label">CPA</div><div className={'metric-value ' + (c.cpa && c.cpa < 15 ? 'good' : c.cpa && c.cpa > 25 ? 'bad' : 'warn')}>{c.cpa !== null ? '€' + c.cpa.toFixed(2) : '—'}</div></div>
            <div className="metric"><div className="metric-label">Impressions</div><div className="metric-value">{(c.impressions || 0).toLocaleString()}</div></div>
          </div>
          <div className="modal-actions">
            <button className="perf-btn primary" onClick={() => onAction(c, 'open-meta')}>↗ Open in Meta Ads</button>
            <button className="perf-btn" onClick={() => onAction(c, 'remix')}>+ Iterate (Studio)</button>
            <button className="perf-btn" onClick={() => onAction(c, 'localize')}>🌐 Localize</button>
          </div>
        </div>
      </div>
    </div>
  );
}
